// Admin-only: per-user trading activity explorer for the /partner dashboard.
// Shows every account that has traded (W/L ladder-merged, net, volume, last
// active) and expands into that user's recent trades so the owner can verify
// what strategies users are actually running.
import { useCallback, useEffect, useState } from "react";
import { Users, ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const SOURCE_LABEL = { pro_trade: "Pro Trade", pro_trade_auto: "Pro Bot", risefall_bot: "24/7 Bot", forex_runner: "Auto-FX", other: "Other" };

const TradeRows = ({ accountId, onUnauthorized }) => {
  const [trades, setTrades] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/user-trades/${encodeURIComponent(accountId)}?limit=50`, { headers: authHeader() })
      .then(({ data }) => { if (alive) setTrades(data?.trades || []); })
      .catch((e) => { if (e?.response?.status === 401) onUnauthorized?.(); if (alive) setTrades([]); });
    return () => { alive = false; };
  }, [accountId, onUnauthorized]);
  if (trades === null) return <div className="px-3 py-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-vts-silver/50" /></div>;
  if (trades.length === 0) return <p className="px-3 py-2 text-[11px] text-vts-silver/45">No trades recorded.</p>;
  return (
    <div className="px-3 pb-2 overflow-x-auto" data-testid={`admin-user-trades-detail-${accountId}`}>
      <table className="w-full text-left">
        <thead>
          <tr className="text-[9px] uppercase tracking-wider text-vts-silver/40">
            <th className="py-1 pr-2">Time</th><th className="py-1 pr-2">Pair</th><th className="py-1 pr-2">Side</th>
            <th className="py-1 pr-2">Strategy</th><th className="py-1 pr-2 text-right">Stake</th>
            <th className="py-1 pr-2 text-right">P/L</th><th className="py-1 pr-2">Status</th><th className="py-1 pr-2">Via</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <tr key={t.contract_id} className="text-[10px] font-mono border-t border-vts-line/40">
              <td className="py-1 pr-2 text-vts-silver/55 whitespace-nowrap">{fmtTime(t.settled_at || t.created_at)}</td>
              <td className="py-1 pr-2 text-white">{t.symbol}</td>
              <td className={`py-1 pr-2 ${t.contract_type === "CALL" ? "text-vts-profit" : "text-vts-loss"}`}>{t.contract_type === "CALL" ? "RISE" : t.contract_type === "PUT" ? "FALL" : t.contract_type}</td>
              <td className="py-1 pr-2 text-vts-silver/70 max-w-[180px]" title={t.strategy}>
                <span className="block truncate">{t.strategy || "—"}{t.ladder_id ? " 🪜" : ""}</span>
                {t.secured_at != null && (
                  <span className="block text-[9px] text-amber-300/90" title="Auto-Secure early close">🔒 secured @ {t.secured_at}%</span>
                )}
                {t.entry_win_rate != null && (
                  <span className="block text-[9px] text-vts-silver/45" data-testid={`admin-trade-why-${t.contract_id}`}>
                    {t.entry_rank === 0 ? "priority" : t.entry_rank ? `#${t.entry_rank}` : "ranked"} · {Number(t.entry_win_rate).toFixed(0)}% WR · {t.entry_trades || 0}n
                  </span>
                )}
              </td>
              <td className="py-1 pr-2 text-right text-vts-silver/70">${Number(t.stake || 0).toFixed(2)}</td>
              <td className={`py-1 pr-2 text-right font-semibold ${(t.profit ?? 0) > 0 ? "text-vts-profit" : (t.profit ?? 0) < 0 ? "text-vts-loss" : "text-vts-silver/50"}`}>
                {t.profit == null ? "—" : `${t.profit >= 0 ? "+" : "−"}$${Math.abs(t.profit).toFixed(2)}`}
              </td>
              <td className={`py-1 pr-2 ${t.status === "won" ? "text-vts-profit" : t.status === "lost" ? "text-vts-loss" : "text-amber-400"}`}>{t.status}</td>
              <td className="py-1 pr-2 text-vts-silver/50">{SOURCE_LABEL[t.source] || t.source || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UserRow = ({ u, onUnauthorized }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`admin-user-row-${u.account_id}`} className="rounded-lg border border-vts-line bg-black/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left flex-wrap">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-vts-silver/50" /> : <ChevronRight className="h-3.5 w-3.5 text-vts-silver/50" />}
        <span className="font-mono text-xs text-white font-bold">{u.display_name || u.account_id}</span>
        {u.display_name && <span className="font-mono text-[10px] text-vts-silver/45">{u.account_id}</span>}
        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${u.is_demo ? "bg-vts-blue/15 text-vts-blue border-vts-blue/40" : "bg-amber-500/15 text-amber-400 border-amber-500/40"}`}>
          {u.is_demo ? "DEMO" : "REAL"}
        </span>
        {u.balance != null && (
          <span data-testid={`admin-user-balance-${u.account_id}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-vts-blue/40 bg-vts-blue/10 text-vts-blue">
            bal ${Number(u.balance).toFixed(2)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-3 text-[11px] font-mono">
          <span className="text-vts-silver/70"><span className="text-vts-profit">{u.wins}W</span>/<span className="text-vts-loss">{u.losses}L</span> · {u.win_rate}%</span>
          <span className={u.net >= 0 ? "text-vts-profit" : "text-vts-loss"}>{u.net >= 0 ? "+" : "−"}${Math.abs(u.net).toFixed(2)}</span>
          <span className="text-vts-silver/45 hidden sm:inline">vol ${u.volume.toFixed(0)}</span>
          <span className="text-vts-silver/45 hidden md:inline">{fmtTime(u.last_trade_at)}</span>
        </span>
        <span className="w-full text-[9px] text-vts-silver/40 pl-5">{(u.sources || []).map((s) => SOURCE_LABEL[s] || s).join(" · ")}</span>
      </button>
      {open && <TradeRows accountId={u.account_id} onUnauthorized={onUnauthorized} />}
    </div>
  );
};

export default function UserTradesAdminPanel({ onUnauthorized }) {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/user-trades", { headers: authHeader() });
      setUsers(data?.users || []);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      setUsers([]);
    } finally { setBusy(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-user-trades-panel" className="mt-6 rounded-xl border border-vts-line bg-vts-panel/60 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-vts-blue" />
        <span className="text-[14px] font-bold text-white">User Trades</span>
        <span className="text-[10px] text-vts-silver/50 font-mono">{users ? `${users.length} traders` : ""} · ladders = 1 trade · tap a user to inspect their trades</span>
        <button onClick={load} disabled={busy} data-testid="admin-user-trades-refresh"
          className="ml-auto flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-vts-line text-vts-silver/70 hover:text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
        </button>
      </div>
      {users === null ? (
        <Loader2 className="h-4 w-4 animate-spin text-vts-silver/50" />
      ) : users.length === 0 ? (
        <p className="text-[12px] text-vts-silver/50" data-testid="admin-user-trades-empty">No user trades recorded yet.</p>
      ) : (
        <div className="space-y-2">{users.map((u) => <UserRow key={u.account_id} u={u} onUnauthorized={onUnauthorized} />)}</div>
      )}
    </div>
  );
}
